import {
  Close,
  KeyboardVoice,
  Mic,
  MicOff,
  Send,
} from "@mui/icons-material";
import {
  Box,
  Fab,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { NovaFace } from "./NovaFace";
import { CustomLabel } from "types/CustomLabel";
import { Requirement } from "types/requirement";
import { Site } from "types/site";
import { Species } from "types/species";
import { Tag } from "types/tag";
import { User } from "types/user";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { RequestFeedbackAction, RequestFeedbackData } from "../types";
import {
  NOVA_COLORS,
  NOVA_NAME,
  NOVA_PRIVACY_NOTE,
  NOVA_TAGLINE,
  NOVA_TITLE,
} from "./character";
import { detectCommand } from "./parseUtterance";
import {
  applyUtterance,
  nextStepFor,
  nextVoiceField,
  VoiceAgentContext,
  VoiceField,
} from "./script";
import { HeardUtterance, useBrowserSpeech } from "./useBrowserSpeech";
import { useVoiceFieldFocus } from "hooks/useVoiceFieldFocus";

type Message = {
  id: string;
  role: "nova" | "user";
  text: string;
};

type FeedbackVoiceAgentProps = {
  step: string;
  data: RequestFeedbackData;
  dispatch: (action: RequestFeedbackAction) => void;
  handleStep: (step: string, action?: string) => void | Promise<void>;
  onBegin: () => void;
  isCoachInitiated: boolean;
  coaches: User[];
  learners: User[];
  requirements: Requirement[];
  species: Species[];
  sites: Site[];
  tags: Tag[];
  customLabels: CustomLabel[];
};

const greeting = (isCoachInitiated: boolean) =>
  `Hi, I'm ${NOVA_NAME}. I'll walk you through this ${
    isCoachInitiated ? "coach feedback" : "feedback request"
  } out loud. Say ready when you want to start.`;

export const FeedbackVoiceAgent: FC<FeedbackVoiceAgentProps> = (props) => {
  const { step, isCoachInitiated } = props;

  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [skippedIds, setSkippedIds] = useState<Set<string>>(new Set());
  const sessionRef = useRef(false);
  const busyRef = useRef(false);
  const skippedRef = useRef<Set<string>>(new Set());
  const propsRef = useRef(props);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const {
    speaking,
    listening,
    interim,
    microphones,
    selectedMicId,
    setSelectedMicId,
    ensureMicrophone,
    speak,
    listen,
    stopAll,
    supported,
  } = useBrowserSpeech();
  const { setActiveFieldId } = useVoiceFieldFocus();

  propsRef.current = props;
  skippedRef.current = skippedIds;

  const contextFrom = (next = propsRef.current): VoiceAgentContext => ({
    step: next.step,
    data: next.data,
    isCoachInitiated: next.isCoachInitiated,
    coaches: next.coaches,
    learners: next.learners,
    requirements: next.requirements,
    species: next.species,
    sites: next.sites,
    tags: next.tags,
    customLabels: next.customLabels,
  });

  const pushMessage = (role: Message["role"], text: string) => {
    setMessages((prev) => [
      ...prev,
      { id: `${role}-${Date.now()}-${prev.length}`, role, text },
    ]);
  };

  useEffect(() => {
    threadRef.current?.scrollTo({
      top: threadRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, interim]);

  const say = useCallback(
    async (text: string) => {
      pushMessage("nova", text);
      if (supported.speak) {
        await speak(text);
      }
    },
    [speak, supported.speak],
  );

  const listenOnce = useCallback(
    async (field?: VoiceField | null): Promise<HeardUtterance> => {
      const empty = { text: "", alternatives: [] };
      if (!sessionRef.current || !supported.listen) return empty;
      try {
        const hints = (field?.options || []).flatMap((option) => [
          option.name,
          ...(option.aliases || []),
        ]);
        return (await listen(hints)) || empty;
      } catch {
        await say("I could not hear that. You can type instead, or tap the mic.");
        return empty;
      }
    },
    [listen, say, supported.listen],
  );

  const askAndListen = useCallback(
    async (field: VoiceField | null) => {
      if (!field || !sessionRef.current || busyRef.current) {
        if (!field) setActiveFieldId(null);
        return;
      }
      setActiveFieldId(field.id);
      busyRef.current = true;
      await say(field.prompt);
      busyRef.current = false;
      const heard = await listenOnce(field);
      if (heard.text) {
        await handleHeard(heard.text, {
          alreadyAsked: true,
          alternatives: heard.alternatives,
        });
      }
    },
    [listenOnce, say, setActiveFieldId],
  );

  const advanceStep = useCallback(async () => {
    const latest = propsRef.current;
    const next = nextStepFor(latest.step, latest.data, latest.isCoachInitiated);
    if (next.begin) {
      latest.onBegin();
      return;
    }
    if (next.step) {
      await latest.handleStep(next.step);
    }
  }, []);

  const handleHeard = useCallback(
    async (
      raw: string,
      options?: { alreadyAsked?: boolean; alternatives?: string[] },
    ) => {
      const text = raw.trim();
      if (!text || !sessionRef.current) return;
      pushMessage("user", text);

      const ctx = contextFrom();
      const field = nextVoiceField(ctx, skippedRef.current);
      const command = detectCommand(text);

      if (command === "stop") {
        sessionRef.current = false;
        setActiveFieldId(null);
        stopAll();
        await say("Okay, I'll stay quiet. Tap the mic when you want me back.");
        return;
      }
      if (command === "repeat") {
        await askAndListen(field);
        return;
      }
      if (command === "back") {
        await propsRef.current.handleStep(propsRef.current.step, "back");
        return;
      }
      if (command === "skip") {
        if (field && field.kind !== "advance" && field.kind !== "review") {
          if (field.required) {
            await say("That one is required. You can type it if that's easier.");
            await askAndListen(field);
            return;
          }
          setSkippedIds((prev) => {
            const next = new Set(prev);
            next.add(field.id);
            skippedRef.current = next;
            return next;
          });
          await say("Skipped.");
          await askAndListen(nextVoiceField(ctx, skippedRef.current));
        }
        return;
      }
      const startOnly =
        command === "start" && text.trim().split(/\s+/).length <= 2;
      if (startOnly && field && field.kind !== "bool") {
        await askAndListen(field);
        return;
      }

      const waitingToBegin = !field || field.kind === "advance" || field.kind === "review";
      if (command === "next" || (waitingToBegin && command === "start")) {
        if (field?.kind === "review") {
          await say(field.prompt);
          return;
        }
        if (field?.kind === "advance") {
          await advanceStep();
          return;
        }
        if (!field && command === "start") {
          await askAndListen(nextVoiceField(ctx, skippedRef.current));
          return;
        }
        await advanceStep();
        return;
      }

      if (waitingToBegin) {
        if (field && !options?.alreadyAsked) await askAndListen(field);
        else if (field) await say(field.prompt);
        return;
      }

      let result = applyUtterance(field, text, ctx);
      if (!result.ok) {
        for (const alternative of options?.alternatives || []) {
          const retry = applyUtterance(field, alternative, ctx);
          if (retry.ok) {
            result = retry;
            break;
          }
        }
      }
      if (result.action) propsRef.current.dispatch(result.action);
      await say(result.confirm);
      if (!result.ok) {
        await askAndListen(field);
        return;
      }
      const justFilled = new Set([field.id]);
      await askAndListen(
        nextVoiceField(ctx, skippedRef.current, justFilled),
      );
    },
    [advanceStep, askAndListen, say, stopAll],
  );

  const prevStepRef = useRef(step);
  useEffect(() => {
    if (!open || !sessionRef.current) {
      prevStepRef.current = step;
      return;
    }
    if (prevStepRef.current === step) return;
    prevStepRef.current = step;
    const field = nextVoiceField(contextFrom(), skippedRef.current);
    if (field && !busyRef.current) {
      void askAndListen(field);
    }
  }, [askAndListen, open, step]);

  const openSession = async () => {
    setOpen(true);
    sessionRef.current = true;
    skippedRef.current = new Set();
    setSkippedIds(new Set());
    setMessages([]);
    try {
      await ensureMicrophone();
    } catch {
      await say(
        "I could not turn the microphone on yet. Pick a mic below, or type.",
      );
    }
    await say(greeting(isCoachInitiated));
    if (!sessionRef.current) return;
    const heard = await listenOnce(
      nextVoiceField(contextFrom(), skippedRef.current),
    );
    if (heard.text) {
      await handleHeard(heard.text, { alternatives: heard.alternatives });
    }
  };

  const onSelectMicrophone = async (deviceId: string) => {
    const wasListening = await setSelectedMicId(deviceId);
    if (!sessionRef.current || speaking) return;
    if (wasListening || supported.listen) {
      const heard = await listenOnce(
        nextVoiceField(contextFrom(), skippedRef.current),
      );
      if (heard.text) {
        await handleHeard(heard.text, { alternatives: heard.alternatives });
      }
    }
  };

  const closeSession = () => {
    sessionRef.current = false;
    setActiveFieldId(null);
    stopAll();
    setOpen(false);
  };

  const onSubmitTyped = async () => {
    if (!typed.trim()) return;
    const value = typed.trim();
    setTyped("");
    sessionRef.current = true;
    await handleHeard(value);
  };

  const onMic = async () => {
    sessionRef.current = true;
    if (listening || speaking) {
      stopAll();
      return;
    }
    if (!supported.listen) {
      await say("Voice input is not available here. Type your answers below.");
      return;
    }
    const heard = await listenOnce(
      nextVoiceField(contextFrom(), skippedRef.current),
    );
    if (heard.text) {
      await handleHeard(heard.text, { alternatives: heard.alternatives });
    }
  };

  return (
    <>
      {!open && (
        <Tooltip title={`Talk with ${NOVA_NAME}`} placement="left">
          <Fab
            color="primary"
            aria-label={`Open ${NOVA_NAME} voice guide`}
            onClick={() => {
              void openSession();
            }}
            sx={{
              position: "fixed",
              right: { xs: 16, md: 28 },
              bottom: { xs: 16, md: 28 },
              zIndex: 1400,
              width: 72,
              height: 72,
              overflow: "hidden",
              background: `radial-gradient(circle at 30% 20%, ${NOVA_COLORS.mintLight} 0%, ${NOVA_COLORS.brand} 48%, ${NOVA_COLORS.brandDeep} 100%)`,
              boxShadow: "0 10px 28px rgba(48, 168, 144, 0.4)",
            }}
          >
            <NovaFace size={56} speaking={speaking} />
          </Fab>
        </Tooltip>
      )}

      {open && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            right: { xs: 8, md: 24 },
            bottom: { xs: 8, md: 24 },
            zIndex: 1400,
            width: { xs: "calc(100vw - 16px)", sm: 380 },
            maxHeight: { xs: "78vh", md: 560 },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 3,
            background: `linear-gradient(180deg, ${NOVA_COLORS.brandDark} 0%, ${NOVA_COLORS.brand} 46%, ${NOVA_COLORS.panel} 46%)`,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ px: 2, pt: 2, pb: 1.5, color: "common.white" }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: `radial-gradient(circle at 30% 20%, ${NOVA_COLORS.mintLight}, ${NOVA_COLORS.brand})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: speaking
                  ? "0 0 0 6px rgba(126, 217, 196, 0.5)"
                  : listening
                    ? "0 0 0 6px rgba(255, 183, 77, 0.4)"
                    : "none",
                transition: "box-shadow 160ms ease",
              }}
            >
              <NovaFace size={52} speaking={speaking} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {NOVA_NAME}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {listening
                  ? "Listening…"
                  : speaking
                    ? "Speaking…"
                    : NOVA_TITLE}
              </Typography>
            </Box>
            <IconButton
              aria-label={`Close ${NOVA_NAME}`}
              onClick={closeSession}
              sx={{ color: "common.white" }}
            >
              <Close />
            </IconButton>
          </Stack>

          <Typography
            variant="caption"
            sx={{ px: 2, pb: 1.5, color: "rgba(255,255,255,0.78)" }}
          >
            {NOVA_TAGLINE}
          </Typography>

          <Box
            ref={threadRef}
            sx={{
              flex: 1,
              minHeight: 180,
              maxHeight: 280,
              overflowY: "auto",
              px: 2,
              py: 1.5,
              backgroundColor: NOVA_COLORS.panel,
            }}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  mb: 1,
                  display: "flex",
                  justifyContent:
                    message.role === "user" ? "flex-end" : "flex-start",
                }}
              >
                <Box
                  sx={{
                    maxWidth: "88%",
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 2,
                    bgcolor:
                      message.role === "user"
                        ? NOVA_COLORS.brand
                        : NOVA_COLORS.mintLight,
                    color:
                      message.role === "user" ? "common.white" : "text.primary",
                  }}
                >
                  <Typography variant="body2">{message.text}</Typography>
                </Box>
              </Box>
            ))}
            {interim && (
              <Typography variant="caption" color="text.secondary">
                {interim}
              </Typography>
            )}
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, pt: 1 }}
          >
            {NOVA_PRIVACY_NOTE}
          </Typography>

          {supported.devices && (
            <Box sx={{ px: 1.5, pt: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="nova-mic-label">Microphone</InputLabel>
                <Select
                  labelId="nova-mic-label"
                  label="Microphone"
                  value={
                    microphones.some((mic) => mic.deviceId === selectedMicId)
                      ? selectedMicId
                      : microphones[0]?.deviceId || ""
                  }
                  onChange={(event) => {
                    void onSelectMicrophone(String(event.target.value));
                  }}
                  disabled={microphones.length === 0}
                >
                  {microphones.length === 0 && (
                    <MenuItem value="" disabled>
                      Allow microphone access to choose a device
                    </MenuItem>
                  )}
                  {microphones.map((mic) => (
                    <MenuItem key={mic.deviceId} value={mic.deviceId}>
                      {mic.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder={`Talk to ${NOVA_NAME}, or type…`}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void onSubmitTyped();
                }
              }}
            />
            <Tooltip title="Send">
              <IconButton
                onClick={() => void onSubmitTyped()}
                sx={{ color: NOVA_COLORS.brandDark }}
              >
                <Send />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                supported.listen
                  ? listening
                    ? "Stop listening"
                    : "Listen"
                  : "Voice input is not available in this browser"
              }
            >
              <IconButton
                color={listening ? "error" : undefined}
                onClick={() => void onMic()}
                sx={listening ? undefined : { color: NOVA_COLORS.brandDark }}
              >
                {supported.listen ? (
                  listening ? (
                    <MicOff />
                  ) : (
                    <Mic />
                  )
                ) : (
                  <KeyboardVoice />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>
      )}
    </>
  );
};
